import Dinero from 'dinero.js';
import { RootState } from '../app/store';
import { DUMMY_ITEM, KEY, POS_ID, POS_NAME } from '../constants';
import { CartItemAdditionTypes } from '../constants/event';
import {
  ModalityType,
  PosProperties,
  PropertyType,
} from '../generated-interfaces/graphql';
import { ISendItemProperties } from '../reducers/orderSlice.props';
import { GenericObjectType } from '../types';
import { convertToMap, findObjectInObjectByKeyValue } from './index';
import { POS_BOOLEAN_PROPERTIES_LIST } from './constants';
import inMemoryGroupIdMapping from '../utils/inMemoryGroupIdMapping';
import logger from '../utils/logger';
import {
  getMenuItemPrice,
  sortChildrenModGroup,
  checkItemInCartItemTree,
  MenuResponses,
} from './menu';
import { computeCartSubtotal, computeCartTax } from './money';
import { GenericMap } from './types';
import { ModSymbolCodeNameMappingType } from './mappings';
import { getMenuUnificationFeatureFlag } from './session-storage';
import {
  BrandMenuUnavailabilityConstraints,
  ParsedMenuItem,
  ParsedModifierGroup,
} from '../types/menu';
import {
  addSuppressUpsellConditions,
  removeSuppressUpsellConditions,
} from './groupedUpsellDeclined';
import { EntityMenuItem } from '../reducers/messagingSlice';
import { isBMItemConstraintNotAvailable } from './menuUnification.utils';
import { IUnavailableEntity } from '../types/brandMenu';
import { MOD_CODES_GROUP_ID } from './menuUnification.constants';
import { OrderStatus } from '../reducers/orderSlice.constants';
import { CartExpandCollapseState } from '../constants/enums';

export interface CartItem extends ParsedMenuItem {
  cartItemId: number;
  modality: ModalityType;
  parentCartModifierGroupId?: string;
  childModifierGroups: GenericMap<CartModifierGroup>;
  // itemOrder?: number;
  itemLevelMemo?: string;
  modcode?: string;
  addedBy?: CartItemAdditionTypes;
  readback?: boolean;
  isUpsell?: boolean;
  isUpsellPrompt?: boolean;
  label?: string;
  autoCombinedCartItemIds?: number[];
  isAutoCombined?: boolean;
  quantity?: number;
  unavailabilityConstraints?: BrandMenuUnavailabilityConstraints;
  readbackName?: string;
  isConverted?: boolean; // Flag to indicate if this item was recently converted
  byPassAutoPrompt?: boolean; // Flag to indicate if the item constraint description should not be auto prompt in Omnibar
}

export interface CartModifierGroup extends ParsedModifierGroup {
  cartModifierGroupId: string;
  menuModifierGroupId: string;
  name: string;
  selectedItems: GenericMap<CartItem>;
}

export interface CartTotal {
  subtotal: number;
  tax: number;
  total: number;
}

export function isValidCartSelector(state: RootState): boolean {
  const cartItems = state.cart.cartItems;
  return !getAllInvalidModGroups(cartItems).length;
}

export function activeCartSelector(state: RootState): boolean {
  return (
    Object.keys(state.cart.cartItems).length > 0 ||
    Object.keys(state.cart.hypothesisCartItems).length > 0
  );
}

export function cartTotalSelector(state: RootState): CartTotal {
  const { restaurantSettings } = state.restaurant.selectedRestaurantDetails;
  const modality = state.cart.modality;
  const cartItems = Object.values(state.cart.cartItems);
  const cartItemsQuantity = state.cart.cartItemsQuantity;
  const menuItems = Object.values(state.menu.fullMenuItems).reduce(
    (acc, menuItem) => {
      if (!menuItem.isModifierOnly) {
        acc[menuItem.itemId] = menuItem;
      }
      return acc;
    },
    {} as GenericMap<ParsedMenuItem>
  );
  const subtotal = computeCartSubtotal(cartItems, modality, cartItemsQuantity);
  const tax = computeCartTax(
    cartItems,
    menuItems,
    modality,
    restaurantSettings,
    cartItemsQuantity
  );
  const total = subtotal.add(tax);
  return {
    subtotal: subtotal.getAmount(),
    tax: tax.getAmount(),
    total: total.getAmount(),
  };
}

export const calculateItemTotal = (
  cartItem: CartItem,
  modality: ModalityType
): Dinero.Dinero => {
  let basePrice = Dinero({ amount: getMenuItemPrice(cartItem, modality) });
  Object.values(cartItem.childModifierGroups).forEach((modGroup) => {
    Object.values(modGroup.selectedItems).forEach(
      (item) => (basePrice = basePrice.add(calculateItemTotal(item, modality)))
    );
  });
  return basePrice;
};

const isNonRequiredUpsellModGroup = (modGroup: ParsedModifierGroup): boolean =>
  modGroup.minimumSelections === 0 && !!modGroup.is_upsell_prompt;

const getNonRequiredUpsellModGroups = (
  modifierGroups: GenericMap<ParsedModifierGroup>
): ParsedModifierGroup[] =>
  Object.values(modifierGroups).filter(isNonRequiredUpsellModGroup);

// Exported for the cart autofill (HITL-1551), which gates on the same predicate
// `getValidCartItems` and the Finish Order button use so it can never disagree
// with HITL about which groups are a problem.
export const isInvalidModGroup = (
  modGroup: ParsedModifierGroup,
  cartModGroup: CartModifierGroup | undefined
): boolean => {
  // If they need to select at least one but no items have been selected
  if (modGroup.minimumSelections > 0 && !cartModGroup) {
    return true;
  }
  // If they don't need to select any and we didn't pass it in, it's fine
  if (!cartModGroup) {
    return false;
  }

  if (modGroup.minimumSelections === 0 && modGroup.maximumSelections === 0) {
    return false;
  }

  const selectedItemsSize = Object.keys(cartModGroup.selectedItems).length;
  if (
    selectedItemsSize < modGroup.minimumSelections ||
    (selectedItemsSize > modGroup.maximumSelections &&
      modGroup.maximumSelections !== -1)
  ) {
    return true;
  }
  return false;
};

export const flatInvalidModGroupsWithDescriptions = (
  cartItem: CartItem
): { modGroups: ParsedModifierGroup[]; descriptions: string[] } => {
  const modGroups: ParsedModifierGroup[] = [];
  const descriptions: string[] = [];
  let sortedModifierGroups: ParsedModifierGroup[] = [];

  sortedModifierGroups = Object.values(cartItem.modifierGroups || {});
  if (!getMenuUnificationFeatureFlag()) {
    sortMods(cartItem, sortedModifierGroups);
  }

  sortedModifierGroups.forEach((modGroup) => {
    const cartModGroup = cartItem.childModifierGroups[modGroup.id];
    if (isInvalidModGroup(modGroup, cartModGroup)) {
      modGroups.push(modGroup);
      descriptions.push(modGroup.description ?? '.');
    }
    if (cartModGroup?.selectedItems) {
      Object.values(cartModGroup.selectedItems).forEach((selectItem) => {
        const childResult = flatInvalidModGroupsWithDescriptions(selectItem);
        modGroups.push(...childResult.modGroups);
        descriptions.push(...childResult.descriptions);
      });
    }
  });

  return { modGroups, descriptions };
};

export const flatNonRequiredUpsellModGroupsWithDescriptions = (
  cartItem: CartItem
): { modGroups: ParsedModifierGroup[]; descriptions: string[] } => {
  const modGroups: ParsedModifierGroup[] = [];
  const descriptions: string[] = [];

  const nonRequiredUpsellModGroups = getNonRequiredUpsellModGroups(
    cartItem.modifierGroups
  );

  if (!getMenuUnificationFeatureFlag()) {
    sortMods(cartItem, nonRequiredUpsellModGroups);
  }

  // Add current level mod groups and descriptions
  for (const modGroup of nonRequiredUpsellModGroups) {
    modGroups.push(modGroup);
    descriptions.push(modGroup.description || '.');
  }

  // Process child items recursively
  const childModifierGroupValues = Object.values(cartItem.childModifierGroups);
  for (const childModGroup of childModifierGroupValues) {
    const selectedItemValues = Object.values(childModGroup.selectedItems);
    for (const selectedItem of selectedItemValues) {
      const childResult =
        flatNonRequiredUpsellModGroupsWithDescriptions(selectedItem);
      modGroups.push(...childResult.modGroups);
      descriptions.push(...childResult.descriptions);
    }
  }

  return { modGroups, descriptions };
};

export const getAllInvalidModGroups = (
  cartItems: GenericMap<CartItem>
): ParsedModifierGroup[] => {
  let invalidModGroups: ParsedModifierGroup[] = [];
  Object.values(cartItems).forEach((cartItem) => {
    invalidModGroups.push(
      ...flatInvalidModGroupsWithDescriptions(cartItem).modGroups
    );
  });
  return invalidModGroups;
};

export const getAllModGroupsWithDescriptions = (
  cartItems: GenericMap<CartItem>,
  suppressExpressUpsells = false
): {
  allModGroups: ParsedModifierGroup[];
  allDescriptions: string[];
} => {
  const allModGroups: ParsedModifierGroup[] = [];
  const allDescriptions: string[] = [];

  // Express mode (do_not_upsell_in_express_mode): a tagged upsell prompt must
  // never be asked, so it must not be offered to the agent as an auto-prompt.
  // Filtering here — where both branches merge — rather than inside them is
  // deliberate on two counts. DQ models upsell prompts as *required*
  // constraints with an explicit decline option, so a tagged prompt arrives via
  // flatInvalidModGroupsWithDescriptions, not the non-required upsell branch.
  // And those branch helpers also feed the red "!" incomplete markers and the
  // HITL-1433 reveal, which must keep showing the group — the agent still needs
  // to see it is unfilled, they just should not be prompted to ask about it.
  // Drops the modGroup and its description together: the omnibar consumes
  // allModGroups[0] and allDescriptions[0] independently.
  // `always_offer` overrides the tag — such a constraint stays in the list so the
  // agent is still prompted to ask it (HITL-1512). This is not redundant with the
  // auto-decline cascade: a constraint with no `upsell_declined_item` can't be
  // declined at all, and AI-added items skip the cascade entirely, so for those
  // this filter is the only thing suppressing the prompt.
  const pushRetained = (result: {
    modGroups: ParsedModifierGroup[];
    descriptions: string[];
  }) => {
    result.modGroups.forEach((modGroup, index) => {
      if (
        suppressExpressUpsells &&
        modGroup.is_upsell_prompt &&
        modGroup.do_not_upsell_in_express_mode &&
        !modGroup.always_offer
      ) {
        return;
      }
      allModGroups.push(modGroup);
      allDescriptions.push(result.descriptions[index]);
    });
  };

  const cartItemCollection = Object.values(cartItems).filter(
    (cartItem) => !cartItem.byPassAutoPrompt
  );
  for (const cartItem of cartItemCollection) {
    pushRetained(flatInvalidModGroupsWithDescriptions(cartItem));
    pushRetained(flatNonRequiredUpsellModGroupsWithDescriptions(cartItem));
  }

  return {
    allModGroups,
    allDescriptions,
  };
};

const sortMods = function (cartItem: CartItem, mods: any[]) {
  const sortOrderMap = convertToMap(cartItem.sortOrder);
  mods.sort(
    (a, b) =>
      (sortOrderMap[a.id]?.sortOrder ?? 0) -
      (sortOrderMap[b.id]?.sortOrder ?? 0)
  );
};

/**
 * For the given cart item, loop through its child modifier groups and its selected items to find the matching mod group recursively
 * @param item Cart item in which the search has to be performed
 * @param modGroupId Find for this modgroup
 * @param selectedItemNodes This is an array into which the selectedItemNode will be populated when the condition matches
 */
export const recursivelyFindSelectedItemNode = (
  item: CartItem,
  modGroupId: string,
  selectedItemNodes: CartItem[],
  rootModGroupId?: string
) => {
  let childModifierGroups = item.childModifierGroups;
  if (rootModGroupId) {
    childModifierGroups = convertToMap(
      Object.values(item.childModifierGroups).filter(
        (childModGroup) => childModGroup.id === rootModGroupId
      )
    );
  }
  Object.values(childModifierGroups).every((childItemEntry) => {
    const { selectedItems } = childItemEntry || {};

    Object.values(selectedItems).every((selectedItem) => {
      const { modifierGroups } = selectedItem;
      const matchedModGroup = Object.values(modifierGroups).filter(
        (modifierGroup) => modifierGroup.id === modGroupId
      );

      if (matchedModGroup.length) {
        selectedItemNodes.push(selectedItem);
        return false;
      } else {
        recursivelyFindSelectedItemNode(
          selectedItem,
          modGroupId,
          selectedItemNodes
        );
        return true;
      }
    });

    return !selectedItemNodes.length;
  });
};

/**
 * Recursively collect all item names from cart items, including nested ones in modifier groups.
 */
export const getAllCartItemNames = (
  cartItems: GenericMap<CartItem>
): string[] => {
  const names: string[] = [];

  const collect = (item: CartItem) => {
    names.push(item.name);

    // Recursively collect from child modifier groups
    if (item.childModifierGroups) {
      Object.values(item.childModifierGroups).forEach((modGroup) => {
        if (modGroup.selectedItems) {
          Object.values(modGroup.selectedItems).forEach((selectedItem) => {
            collect(selectedItem);
          });
        }
      });
    }
  };

  Object.values(cartItems).forEach((item) => {
    collect(item);
  });

  return names;
};

let pathToGroupId: string = '';

export const getCartItemProperties = (
  cartItem: CartItem,
  groupId: string[],
  path_to_entity: string[],
  restaurantCode: string,
  itemsQuantity: Record<string, number>,
  isModifier = false
) => {
  const groupIdMappings = inMemoryGroupIdMapping.getGroupIdMapping(
    restaurantCode || ''
  );

  const boolList = ['true', 'false', ''];
  let posNameAdded = false;
  const posProperties = Object.values(cartItem.posProperties).reduce(
    (acc, { key, value }) => {
      if (key === POS_ID && value !== DUMMY_ITEM) {
        groupId.push(value);
      }
      if (key === POS_NAME && !posNameAdded) {
        path_to_entity.push(value);
        posNameAdded = true;
      }
      acc[key] =
        POS_BOOLEAN_PROPERTIES_LIST.includes(key) &&
        boolList.includes(value.toLowerCase())
          ? value.toLowerCase() === boolList[0]
          : value;
      return acc;
    },
    {} as { [key: string]: any }
  );

  if (cartItem.modcode) {
    posProperties.modcode = cartItem.modcode;
  }

  pathToGroupId = groupId.slice(0, -1).join('__');
  if (groupIdMappings[pathToGroupId]) {
    posProperties['group_id'] = groupIdMappings[pathToGroupId];
    posProperties['component_id'] = groupIdMappings[pathToGroupId];
    pathToGroupId = '';
  } else {
    logger.warn({
      restaurantCode,
      message: `Unable to find component id for ${pathToGroupId}`,
    });
  }
  const posIDObject: GenericObjectType = findObjectInObjectByKeyValue(
    cartItem.posProperties,
    KEY,
    POS_ID
  );
  const childItems = sortChildrenModGroup(cartItem).reduce((acc, modGroup) => {
    const mod_group_path_to_entity = [...path_to_entity];
    const posNamePosProperty = Object.values(
      modGroup?.posProperties || {}
    ).find(({ key }) => key === POS_NAME);
    if (posNamePosProperty) {
      mod_group_path_to_entity.push(posNamePosProperty.value);
    }
    if (posIDObject?.value !== DUMMY_ITEM) groupId.push(modGroup.prpName);
    const selectedItems = Object.values(modGroup.selectedItems).map((item) => {
      const cartItem = getCartItemProperties(
        item,
        [...groupId],
        [...mod_group_path_to_entity],
        restaurantCode,
        itemsQuantity,
        true
      );
      return cartItem;
    });
    groupId.pop();
    acc.push(...selectedItems);
    return acc;
  }, [] as ISendItemProperties[]);

  const { isUpsell, isUpsellPrompt, label } = cartItem;

  const itemData: ISendItemProperties = {
    id: parseInt(cartItem.id),
    name: cartItem.name,
    memo: cartItem.itemLevelMemo || undefined,
    quantity: isModifier ? 1 : itemsQuantity[cartItem.cartItemId] || 1,
    price: (getMenuItemPrice(cartItem, cartItem.modality) / 100).toFixed(2), // Apply price override
    options: childItems,
    children: childItems,
    pos_specific_properties: posProperties,
    added_by: cartItem.addedBy || CartItemAdditionTypes.human,
    path_to_entity,
    ...(isUpsell !== undefined && { is_upsell: isUpsell }),
    ...(isUpsellPrompt !== undefined && { is_upsell_prompt: isUpsellPrompt }),
    ...(label && { label }),
  };
  return itemData;
};

// Return the item if it exists (or) check in synonyms for mod_codes
export const getBrandMenuItem = ({
  modGroup,
  modGroupId,
  menuItemId,
  bmItemsSynonyms,
}: {
  modGroup: ParsedModifierGroup;
  modGroupId: string;
  menuItemId: string;
  bmItemsSynonyms?: Record<string, Array<string>>;
}) => {
  let actualMenuItem = modGroup?.menuItems[menuItemId];

  if (
    !actualMenuItem &&
    modGroupId.toLocaleLowerCase().startsWith(MOD_CODES_GROUP_ID) &&
    bmItemsSynonyms
  ) {
    const menuItemIdLowerCase = menuItemId.toLocaleLowerCase();

    const matchingItemId = Object.entries(bmItemsSynonyms).find(
      ([key, value]) => {
        if (
          value &&
          value
            .map((entry) => entry.toLocaleLowerCase())
            .includes(menuItemIdLowerCase)
        ) {
          return true;
        }
        return false;
      }
    );

    if (matchingItemId?.length) {
      actualMenuItem = modGroup?.menuItems[matchingItemId[0]];
    }
  }

  return actualMenuItem;
};

export const selectModifierInCartItem = (
  cartItem: CartItem,
  menuItemId: string,
  modGroupId: string,
  selected: boolean,
  modCode: string,
  modSymbolMapping: ModSymbolCodeNameMappingType,
  posSettings: GenericMap<PosProperties>,
  modality: ModalityType,
  pathToItem?: string,
  rootModGroupId?: string,
  readback?: boolean,
  quantity?: number,
  bmItemsSynonyms?: Record<string, Array<string>>
): CartItem | undefined => {
  const isBrandMenu = getMenuUnificationFeatureFlag();
  let node = cartItem;
  let sequenceId = cartItem.cartItemId;
  const selectedItemNodes: CartItem[] = [];
  if (pathToItem) {
    node =
      checkItemInCartItemTree({
        cartItem: node,
        pathToItem,
        fromSelectModifier: true,
      }) || node;
  } else {
    recursivelyFindSelectedItemNode(
      cartItem,
      modGroupId,
      selectedItemNodes,
      rootModGroupId
    );
  }
  if (selectedItemNodes.length) {
    node = selectedItemNodes[0];
  }
  if (!node) {
    return undefined;
  }

  const modGroup = node?.modifierGroups[modGroupId];
  const menuItem = isBrandMenu
    ? getBrandMenuItem({ modGroup, modGroupId, menuItemId, bmItemsSynonyms })
    : modGroup?.menuItems[menuItemId];
  if (!modGroup || !menuItem) {
    return undefined;
  }
  if (!node.childModifierGroups[modGroupId]) {
    node.childModifierGroups[modGroupId] = {
      ...node.modifierGroups[modGroupId],
      cartModifierGroupId: String(sequenceId++),
      menuModifierGroupId: modGroupId,
      name: modGroup.name,
      selectedItems: {},
      posProperties: posSettings
        ? convertToMap(
            Object.values(posSettings).filter(
              (setting) =>
                setting.objectPrimaryKey === modGroupId &&
                setting.propertyType === PropertyType.ModifierGroup
            )
          )
        : undefined,
    };
  }

  const existing = node.childModifierGroups[modGroupId].selectedItems;
  const menuItemIsAlreadySelected = existing && existing[menuItemId];

  if (modGroup.maximumSelections === 1) {
    // Remove other choices automatically
    // Need to remove other choices when select different choice
    if (!menuItemIsAlreadySelected) {
      node.childModifierGroups[modGroup.id].selectedItems = {};
    }
  } else if (
    selected &&
    existing &&
    modGroup.maximumSelections === Object.keys(existing).length
  ) {
    // Do not allow selection of more then maximumSelections
    return undefined;
  }

  if (!selected) {
    delete node.childModifierGroups[modGroup.id].selectedItems[menuItem.itemId];
    return undefined;
  }

  // Select a new modifier or update the modcode
  const mappedModCode = modSymbolMapping[modCode]?.code || '';

  let newQuantity = 0;
  if (quantity) {
    // Quanity is passed only for BM auto combined item
    const existingQuantity =
      node.childModifierGroups[modGroup.id].selectedItems[menuItem.itemId]
        ?.quantity || 0;
    newQuantity = existingQuantity + quantity;
  }

  const insertedModifier: CartItem = {
    ...menuItem,
    modality,
    cartItemId: sequenceId++,
    childModifierGroups: {},
    modcode: mappedModCode,
    readback,
    ...(quantity ? { quantity: newQuantity } : {}),
  };
  node.childModifierGroups[modGroup.id].selectedItems[menuItem.itemId] =
    insertedModifier;

  // If modifier readback is true, make its parent readback also as true if it is false
  if (readback && !node.readback) {
    node.readback = true;
  }

  return insertedModifier;
};

/**
 * Update groupedUpsellDeclined based on the just-applied modifier selection.
 * Runs for both human and hypothesis flows so HITL takeover inherits the
 * decline state — but it does NOT cascade auto-decline. Human flows run the
 * cascade separately via applyUpsellConditionsToCartItem.
 * - decline selected            -> add conditions
 * - decline unselected          -> remove conditions
 * - single-choice upsell picked -> decline auto-deselected, remove conditions
 */
export const trackSuppressUpsellOnSelection = (
  cartItem: CartItem | undefined,
  modGroupId: string,
  menuItemId: string,
  selected: boolean
): void => {
  if (!cartItem) return;
  const modGroup = cartItem.modifierGroups?.[modGroupId];
  const suppressConditions = modGroup?.suppress_upsell_conditions ?? [];
  if (!modGroup || suppressConditions.length === 0) return;

  const menuItemName = modGroup.menuItems?.[menuItemId]?.name;
  if (!menuItemName) return;
  const isDecline = (modGroup.upsell_decline_items ?? []).includes(
    menuItemName
  );
  const isUpsell = (modGroup.upsell_items ?? []).includes(menuItemName);
  const isSingleChoice = modGroup.maximumSelections === 1;

  if (isDecline && selected) {
    addSuppressUpsellConditions(suppressConditions);
  } else if (isDecline && !selected) {
    removeSuppressUpsellConditions(suppressConditions);
  } else if (isUpsell && selected && isSingleChoice) {
    removeSuppressUpsellConditions(suppressConditions);
  }
};

export const processDefaultSelectedModifiers = (
  cartItem: CartItem,
  modSymbolMapping: ModSymbolCodeNameMappingType,
  posSettings: MenuResponses['posSettings'],
  modality: ModalityType,
  unavailableItemsByName: GenericMap<IUnavailableEntity[]>
) => {
  // Loop through all modifier groups in the cart item and select the default items if they exist
  Object.values(cartItem.modifierGroups).forEach((modGroup) => {
    if (
      modGroup.defaultSelectedItemIds &&
      modGroup.defaultSelectedItemIds.length
    ) {
      modGroup.defaultSelectedItemIds.forEach((itemId: string | number) => {
        try {
          if (
            !(
              getMenuUnificationFeatureFlag() &&
              isBMItemConstraintNotAvailable({
                unavailableItemsByName,
                itemName: itemId.toString(),
                cartItem,
                constraintName: modGroup.name,
              })
            )
          ) {
            selectModifierInCartItem(
              cartItem,
              itemId.toString(),
              modGroup.id,
              true,
              '',
              modSymbolMapping,
              posSettings,
              modality
            );
          }
        } catch (err) {
          logger.error('Error selecting default item', err);
        }

        // If there are child items, recursively process their default selected modifiers
        const childModGroups = Object.values(cartItem.childModifierGroups);
        if (childModGroups.length) {
          childModGroups.forEach((childModGroup) => {
            const selectedItems = Object.values(childModGroup.selectedItems);
            if (selectedItems.length) {
              selectedItems.forEach((item) =>
                processDefaultSelectedModifiers(
                  item,
                  modSymbolMapping,
                  posSettings,
                  modality,
                  unavailableItemsByName
                )
              );
            }
          });
        }
      });
    }
  });
};

export const getPosPropertyValue = (
  posPropertiesRecord: Record<string, PosProperties>,
  posPropKey: string
) => {
  return Object.values(posPropertiesRecord).find(
    (posProp) => posProp.key === posPropKey
  )?.value;
};

export const getCartItemNonDummyNode = (cartItem: CartItem) => {
  const cartItemPosId = getPosPropertyValue(cartItem.posProperties, POS_ID);
  let node = cartItem;

  const childModifierGroups = Object.values(cartItem.childModifierGroups);
  if (
    cartItemPosId === DUMMY_ITEM &&
    childModifierGroups.length &&
    Object.values(childModifierGroups[0].selectedItems).length
  ) {
    node = Object.values(childModifierGroups[0].selectedItems)[0];
  }
  return node;
};

export const getValidCartItems = (cartItems: Record<string, CartItem> = {}) => {
  return Object.values(cartItems)
    .filter(
      (cartItem) =>
        flatInvalidModGroupsWithDescriptions(cartItem).modGroups.length === 0 ||
        cartItem.isAutoCombined
    )
    .reduce((acc, entry) => {
      acc[entry.cartItemId] = entry;
      return acc;
    }, {} as GenericMap<CartItem>);
};

export const getAutoCombinedCartItemIdsCount = (
  autoCombinedCartItemIds: number[]
) => {
  return autoCombinedCartItemIds.reduce((acc, currentValue) => {
    const currentValueToString = currentValue.toString();
    if (Object.keys(acc).includes(currentValueToString)) {
      acc[currentValueToString] += 1;
    } else {
      acc[currentValueToString] = 1;
    }
    return acc;
  }, {} as Record<string, number>);
};

export const convertHypothesisItems = (hypoItems: EntityMenuItem[]) => {
  return hypoItems.map((item) => {
    const {
      id,
      name,
      pos_specific_properties,
      quantity,
      price,
      children,
      path_to_entity,
    } = item;
    const childItems = convertHypothesisItems(children);
    const itemProperties: ISendItemProperties = {
      id,
      name,
      quantity,
      price: price.toString(),
      options: childItems, // deprecate
      children: childItems,
      added_by: CartItemAdditionTypes.AI,
      pos_specific_properties,
      path_to_entity,
    };
    return itemProperties;
  });
};

export const getSelectedCartItem = (cartItems: GenericMap<CartItem>) => {
  const selectedCartItem = Object.values(cartItems).length
    ? Object.values(cartItems)[Object.values(cartItems).length - 1]
    : null;

  return selectedCartItem;
};

export const shouldCollapse = function (collapseState: number, status: any) {
  return (
    (collapseState === CartExpandCollapseState.AUTO_COLLAPSE ||
      collapseState === CartExpandCollapseState.MANUAL_COLLAPSE) &&
    status &&
    status !== OrderStatus.failed
  );
};
